/**
 * Product Gallery Module - 2025+ Modern Implementation
 * Handles images, videos, 3D models, zoom, thumbnails, and variant switching
 */
class ProductGallery {
  constructor(container) {
    if (!container) return;
    
    this.container = container;
    this.thumbnails = container.querySelectorAll('.labratgifts-product-gallery__thumbnail');
    this.slides = container.querySelectorAll('.labratgifts-product-gallery__slide');
    this.zoomEnabled = container.dataset.zoom === 'true';
    this.currentIndex = 0;
    this.media = [];
    
    this.init();
  }
  
  init() {
    // Build media array with type detection
    this.buildMediaList();
    
    // Setup interactions
    this.setupThumbnailClicks();
    this.setupNavigation();
    this.setupKeyboardNav();
    
    // Setup zoom for desktop
    if (this.zoomEnabled && this.isDesktop()) {
      this.setupZoom();
    }
    
    // Listen for variant changes
    this.setupVariantListener();
    
    // Load Shopify media features for videos and 3D models
    this.loadShopifyMediaFeatures();
  }
  
  buildMediaList() {
    this.media = Array.from(this.slides).map((slide, index) => {
      const mediaId = slide.dataset.mediaId;
      const mediaType = this.detectMediaType(slide);
      const thumbnail = this.thumbnails[index];
      
      return {
        id: mediaId,
        type: mediaType,
        slide: slide,
        thumbnail: thumbnail,
        element: this.getMediaElement(slide, mediaType)
      };
    });
  }
  
  detectMediaType(slide) {
    if (slide.querySelector('.labratgifts-product-gallery__image')) return 'image';
    if (slide.querySelector('.labratgifts-product-gallery__video')) return 'video';
    if (slide.querySelector('.labratgifts-product-gallery__external-video')) return 'external_video';
    if (slide.querySelector('.labratgifts-product-gallery__model')) return 'model';
    return 'image';
  }
  
  getMediaElement(slide, type) {
    const selectors = {
      'image': '.labratgifts-product-gallery__image',
      'video': 'video',
      'external_video': 'iframe',
      'model': 'model-viewer'
    };
    return slide.querySelector(selectors[type]);
  }
  
  isDesktop() {
    return window.innerWidth >= 768;
  }
  
  setupThumbnailClicks() {
    this.thumbnails.forEach((thumbnail, index) => {
      thumbnail.addEventListener('click', (e) => {
        e.preventDefault();
        this.goToSlide(index);
      });
    });
  }
  
  goToSlide(index) {
    if (index < 0 || index >= this.slides.length) return;
    
    // Remove active states
    this.thumbnails.forEach(thumb => thumb.classList.remove('active'));
    this.slides.forEach(slide => slide.classList.remove('active'));
    
    // Add active states
    this.thumbnails[index]?.classList.add('active');
    this.slides[index]?.classList.add('active');
    
    // Update current index
    this.currentIndex = index;
    
    // Handle media playback based on type
    this.handleMediaPlayback(index);
    
    // Update aria attributes
    this.slides[index]?.querySelector('img, video, model-viewer')?.setAttribute('aria-current', 'true');
  }
  
  handleMediaPlayback(index) {
    const mediaItem = this.media[index];
    if (!mediaItem) return;
    
    // Pause all other media first
    this.pauseAllMedia();
    
    // Handle different media types
    switch (mediaItem.type) {
      case 'video':
        this.playVideo(mediaItem.element);
        break;
      case 'external_video':
        this.playExternalVideo(mediaItem.element);
        break;
      case 'model':
        this.activateModel(mediaItem.element);
        break;
    }
  }
  
  playVideo(videoElement) {
    if (videoElement && videoElement.tagName === 'VIDEO') {
      videoElement.muted = false;
      videoElement.play().catch(err => {
        console.log('Video autoplay failed, trying muted:', err);
        videoElement.muted = true;
        videoElement.play().catch(e => console.log('Video playback failed:', e));
      });
    }
  }
  
  playExternalVideo(iframeElement) {
    if (iframeElement && iframeElement.tagName === 'IFRAME') {
      // For YouTube/Vimeo, attempt autoplay via iframe API
      const src = iframeElement.src;
      if (src.includes('youtube.com') || src.includes('youtu.be')) {
        if (!src.includes('autoplay=1')) {
          iframeElement.src = src + (src.includes('?') ? '&' : '?') + 'autoplay=1&mute=1';
        }
      } else if (src.includes('vimeo.com')) {
        if (!src.includes('autoplay=1')) {
          iframeElement.src = src + (src.includes('?') ? '&' : '?') + 'autoplay=1&muted=1';
        }
      }
    }
  }
  
  activateModel(modelElement) {
    if (modelElement && modelElement.tagName === 'MODEL-VIEWER') {
      // Trigger model viewer interaction
      modelElement.setAttribute('autoplay', '');
      modelElement.setAttribute('auto-rotate', '');
      
      // Focus on the model
      setTimeout(() => {
        if (typeof modelElement.play === 'function') {
          modelElement.play();
        }
      }, 100);
    }
  }
  
  pauseAllMedia() {
    this.media.forEach(mediaItem => {
      if (mediaItem.type === 'video' && mediaItem.element) {
        mediaItem.element.pause();
      } else if (mediaItem.type === 'external_video' && mediaItem.element) {
        // Stop external videos by reloading iframe
        const src = mediaItem.element.src;
        if (src) {
          mediaItem.element.src = src.replace(/autoplay=1/g, 'autoplay=0');
        }
      } else if (mediaItem.type === 'model' && mediaItem.element) {
        mediaItem.element.removeAttribute('autoplay');
      }
    });
  }
  
  setupZoom() {
    const images = this.container.querySelectorAll('.labratgifts-product-gallery__image');
    const zoomButtons = this.container.querySelectorAll('.labratgifts-product-gallery__zoom-btn');
    
    const isInQuickView = this.container.closest('#quick-view-modal');
    
    // Check if the inline lightbox script exists (from product-gallery.liquid)
    // If so, let it handle image clicks instead to avoid conflicts
    const hasLightbox = document.getElementById('productLightbox');
    
    images.forEach(img => {
      const zoomUrl = img.dataset.zoomImage;
      if (!zoomUrl) return;
      
      img.style.cursor = 'zoom-in';
      
      // Only add click handlers if no lightbox or in quick view
      if (!hasLightbox || isInQuickView) {
        img.addEventListener('click', (e) => {
          e.preventDefault();
          
          if (isInQuickView) {
            document.dispatchEvent(new CustomEvent('quickview:zoom-open', {
              detail: { currentIndex: this.currentIndex }
            }));
          } else {
            this.createZoomView(img, zoomUrl);
          }
        });
      }
    });
    
    // Only add button handlers if no lightbox (quick view uses createZoomView)
    if (!hasLightbox || isInQuickView) {
      zoomButtons.forEach(btn => {
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          
          if (isInQuickView) {
            document.dispatchEvent(new CustomEvent('quickview:zoom-open', {
              detail: { currentIndex: this.currentIndex }
            }));
          } else {
            const slide = btn.closest('.labratgifts-product-gallery__slide');
            if (slide) {
              const img = slide.querySelector('.labratgifts-product-gallery__image');
              const zoomUrl = img?.dataset.zoomImage;
              if (zoomUrl) {
                this.createZoomView(img, zoomUrl);
              }
            }
          }
        });
      });
    }
  }
  
  createZoomView(img, zoomUrl) {
    const slide = img.closest('.labratgifts-product-gallery__slide');
    if (!slide) return;
    
    const allImages = Array.from(this.container.querySelectorAll('.labratgifts-product-gallery__image'));
    const currentImageIndex = allImages.indexOf(img);
    
    const overlay = document.createElement('div');
    overlay.className = 'labratgifts-zoom-overlay';
    overlay.innerHTML = `
      <div class="labratgifts-zoom-overlay__container">
        <button type="button" class="labratgifts-zoom-overlay__close" aria-label="Close zoom">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
          </svg>
        </button>
        
        <div class="labratgifts-zoom-overlay__content">
          <div class="labratgifts-zoom-gallery">
            <div class="labratgifts-zoom-gallery__main">
              <div class="labratgifts-zoom-gallery__viewport">
                <img src="${zoomUrl}" alt="${img.alt}" class="labratgifts-zoom-gallery__image">
              </div>
              
              ${allImages.length > 1 ? `
                <button type="button" class="labratgifts-zoom-gallery__nav labratgifts-zoom-gallery__nav--prev" aria-label="Previous image">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <path d="M15 18l-6-6 6-6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                </button>
                <button type="button" class="labratgifts-zoom-gallery__nav labratgifts-zoom-gallery__nav--next" aria-label="Next image">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <path d="M9 18l6-6-6-6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                </button>
              ` : ''}
            </div>
          </div>
        </div>
      </div>
    `;
    
    document.body.appendChild(overlay);
    document.body.style.overflow = 'hidden';
    
    requestAnimationFrame(() => {
      overlay.classList.add('active');
    });
    
    const closeOverlay = () => {
      overlay.classList.remove('active');
      document.body.style.overflow = '';
      setTimeout(() => overlay.remove(), 300);
    };
    
    overlay.querySelector('.labratgifts-zoom-overlay__close').addEventListener('click', closeOverlay);
    overlay.addEventListener('click', (e) => {
      if (e.target === overlay || e.target.classList.contains('labratgifts-zoom-overlay__container')) {
        closeOverlay();
      }
    });
    
    document.addEventListener('keydown', function escHandler(e) {
      if (e.key === 'Escape') {
        closeOverlay();
        document.removeEventListener('keydown', escHandler);
      }
    });
    
    // Navigation within zoom
    let currentZoomIndex = currentImageIndex;
    const updateZoomImage = (newIndex) => {
      if (newIndex < 0 || newIndex >= allImages.length) return;
      currentZoomIndex = newIndex;
      const newImg = allImages[newIndex];
      const newZoomUrl = newImg.dataset.zoomImage || newImg.src;
      overlay.querySelector('.labratgifts-zoom-gallery__image').src = newZoomUrl;
    };
    
    const prevBtn = overlay.querySelector('.labratgifts-zoom-gallery__nav--prev');
    const nextBtn = overlay.querySelector('.labratgifts-zoom-gallery__nav--next');
    
    if (prevBtn) {
      prevBtn.addEventListener('click', () => {
        updateZoomImage(currentZoomIndex > 0 ? currentZoomIndex - 1 : allImages.length - 1);
      });
    }
    
    if (nextBtn) {
      nextBtn.addEventListener('click', () => {
        updateZoomImage(currentZoomIndex < allImages.length - 1 ? currentZoomIndex + 1 : 0);
      });
    }
  }
  
  setupNavigation() {
    const prevBtn = this.container.querySelector('.labratgifts-product-gallery__nav--prev');
    const nextBtn = this.container.querySelector('.labratgifts-product-gallery__nav--next');
    
    if (prevBtn) {
      prevBtn.addEventListener('click', () => {
        const newIndex = this.currentIndex - 1;
        this.goToSlide(newIndex < 0 ? this.slides.length - 1 : newIndex);
      });
    }
    
    if (nextBtn) {
      nextBtn.addEventListener('click', () => {
        const newIndex = this.currentIndex + 1;
        this.goToSlide(newIndex >= this.slides.length ? 0 : newIndex);
      });
    }
  }
  
  setupKeyboardNav() {
    this.container.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        const newIndex = this.currentIndex - 1;
        this.goToSlide(newIndex < 0 ? this.slides.length - 1 : newIndex);
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        const newIndex = this.currentIndex + 1;
        this.goToSlide(newIndex >= this.slides.length ? 0 : newIndex);
      }
    });
  }
  
  setupVariantListener() {
    // Listen for variant change events from product form
    document.addEventListener('product:variant-change', (event) => {
      if (event.detail && event.detail.variant) {
        this.handleVariantImageChange(event.detail.variant);
      }
    });
    
    // Also listen directly to variant selectors as fallback
    const variantSelectors = document.querySelectorAll('input[name="id"], select[name="id"]');
    variantSelectors.forEach(selector => {
      selector.addEventListener('change', (e) => {
        const variantId = e.target.value;
        this.findAndSwitchToVariantImage(variantId);
      });
    });
  }
  
  handleVariantImageChange(variant) {
    // FIXED: Get media ID from the hidden select option's data attribute
    // The variant JSON doesn't include featured_media.id, so we read from DOM
    // Scope to nearest product section to handle Quick View and multiple product forms
    const variantId = variant.id;
    
    // Find the product section that contains this gallery (try multiple selectors)
    const productSection = this.container.closest('.labratgifts-product') 
      || this.container.closest('[data-product-section]')
      || this.container.closest('.quick-view-modal__content')
      || this.container.closest('#quick-view-modal');
    
    let hiddenOption = null;
    
    if (productSection) {
      hiddenOption = productSection.querySelector(`option[data-variant-id="${variantId}"]`);
    }
    
    // Fallback to document-wide search if not found in section
    if (!hiddenOption) {
      hiddenOption = document.querySelector(`option[data-variant-id="${variantId}"]`);
    }
    
    if (hiddenOption && hiddenOption.dataset.variantMediaId) {
      const mediaId = hiddenOption.dataset.variantMediaId.toString();
      const mediaIndex = this.media.findIndex(m => m.id === mediaId);
      
      if (mediaIndex !== -1) {
        this.goToSlide(mediaIndex);
      }
      return;
    }
    
    // Fallback: Try using featured_media from variant object if available
    if (variant.featured_media && variant.featured_media.id) {
      const mediaId = variant.featured_media.id.toString();
      const mediaIndex = this.media.findIndex(m => m.id === mediaId);
      
      if (mediaIndex !== -1) {
        this.goToSlide(mediaIndex);
      }
    }
  }
  
  findAndSwitchToVariantImage(variantId) {
    // Find media associated with this variant from the hidden select options
    // Scope to nearest product section to handle multiple product forms
    const productSection = this.container.closest('.labratgifts-product') 
      || this.container.closest('[data-product-section]')
      || this.container.closest('.quick-view-modal__content')
      || this.container.closest('#quick-view-modal');
    
    let variantOption = null;
    
    if (productSection) {
      variantOption = productSection.querySelector(`option[data-variant-id="${variantId}"]`);
    }
    
    // Fallback to document-wide search
    if (!variantOption) {
      variantOption = document.querySelector(`option[data-variant-id="${variantId}"]`);
    }
    
    if (variantOption && variantOption.dataset.variantMediaId) {
      const mediaId = variantOption.dataset.variantMediaId.toString();
      const mediaIndex = this.media.findIndex(m => m.id === mediaId);
      
      if (mediaIndex !== -1) {
        this.goToSlide(mediaIndex);
      }
    }
  }
  
  loadShopifyMediaFeatures() {
    // Load Shopify's media features for videos and 3D models
    const hasVideo = this.media.some(m => m.type === 'video' || m.type === 'external_video');
    const hasModel = this.media.some(m => m.type === 'model');
    
    if (window.Shopify && window.Shopify.loadFeatures) {
      const features = [];
      if (hasVideo) features.push('video-ui');
      if (hasModel) features.push('model-viewer-ui');
      
      if (features.length > 0) {
        window.Shopify.loadFeatures(features, (error) => {
          if (error) {
            console.error('Error loading Shopify media features:', error);
          }
        });
      }
    }
  }
}

// Initialize galleries when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    document.querySelectorAll('.labratgifts-product-gallery').forEach(gallery => {
      new ProductGallery(gallery);
    });
  });
} else {
  document.querySelectorAll('.labratgifts-product-gallery').forEach(gallery => {
    new ProductGallery(gallery);
  });
}
